import { CustomListItemId } from "@sp-crm/core";
import { SupportEmailLink } from "components/app/support-email-link";
import { CustomListMultiselect } from "components/shared/custom-list-multiselect";
import { AutosavingInput } from "components/ui/autosaving-input";
import { InlineBanner } from "components/ui/inline-banner";
import {
    MessageTrackingLevel,
    useGetMessagingConfigurationQuery,
} from "generated/graphql";
import { produce } from "immer";
import React from "react";
import { stableQueryOptions } from "util/requests";
import {
    customListKeyForEntityType,
    entityTypeNoun,
    entityTypeStatusFilterLabel,
    findEmailOpenedStatusFieldMatcher,
} from "./manage-workflows-helper";
import { EditableWorkflow } from "./types";

interface EmailOpenedTriggerEditorProps {
    workflow: EditableWorkflow;
    onChange: (newWorkflow: EditableWorkflow) => void;
}

export const EmailOpenedTriggerEditor: React.FC<
    EmailOpenedTriggerEditorProps
> = props => {
    const { workflow, onChange } = props;

    const getMessagingConfiguration = useGetMessagingConfigurationQuery(
        {},
        stableQueryOptions(),
    );

    const handleChange = (selectedIds: CustomListItemId[]) => {
        const newWorkflow = produce(workflow, draft => {
            const matcher = findEmailOpenedStatusFieldMatcher(draft);

            if (selectedIds.length > 0) {
                if (matcher) {
                    matcher.fieldName = "statusListItem";
                    matcher.current.values = selectedIds;
                } else {
                    draft.trigger.emailOpened!.entityFieldMatchers.push({
                        fieldName: "statusListItem",
                        current: {
                            values: selectedIds,
                            specialValue: null,
                        },
                    });
                }
            } else {
                draft.trigger.emailOpened!.entityFieldMatchers =
                    draft.trigger.emailOpened!.entityFieldMatchers.filter(
                        m => m.fieldName !== "statusListItem",
                    );
            }
        });

        onChange(newWorkflow);
    };

    const trigger = workflow.trigger?.emailOpened;

    const fieldMatcher = findEmailOpenedStatusFieldMatcher(workflow);

    if (!trigger) {
        return null;
    }

    return (
        <div className="space-y-2">
            {getMessagingConfiguration.data?.getMessagingConfiguration?.tenant
                .trackingLevel === MessageTrackingLevel.Message ? (
                <InlineBanner type="warning">
                    This workflow is optimized for agencies that have per-recipient
                    tracking enabled for email. We recommend contacting{" "}
                    <SupportEmailLink /> to upgrade your email tracking.
                </InlineBanner>
            ) : null}
            <div>
                <p>{entityTypeStatusFilterLabel(workflow.attachTo)}</p>
                <CustomListMultiselect
                    disabled={!workflow.regionVisibility?.editable}
                    customListKey={customListKeyForEntityType(workflow.attachTo)}
                    value={fieldMatcher?.current?.values || []}
                    onChange={handleChange}
                />
                <div className="ml-6 text-gray-500 group-hover:text-gray-900 text-sm">
                    Optionally filter email open workflows to these{" "}
                    {entityTypeNoun(workflow.attachTo)} statuses. Leave empty to trigger
                    on any status.
                </div>
            </div>
            <div>
                <AutosavingInput
                    label="Inactivity threshold (days)"
                    type="number"
                    min="0"
                    step="1"
                    disabled={!workflow.regionVisibility?.editable}
                    initial={(trigger.activityThresholdDays || 0).toString()}
                    onCommit={value => {
                        onChange(
                            produce(workflow, draft => {
                                const newValue = parseInt(value, 10);

                                draft.trigger.emailOpened!.activityThresholdDays = isNaN(
                                    newValue,
                                )
                                    ? 0
                                    : newValue;
                            }),
                        );
                    }}
                />
                <div className="ml-6 text-gray-500 group-hover:text-gray-900 text-sm">
                    The number of days since the last email open to trigger the workflow.
                    If a recipient has already opened the email within this threshold, the
                    workflow will not trigger.
                </div>
            </div>
        </div>
    );
};
